<template>
    <div class="mt-6 content reallocate-hours">
        <h3 class="has-text-white"><i class="fad fa-fw fa-arrows-turn-right mr-3"></i>Reallocate Hours</h3>

        <p class="mt-6 has-text-white" v-if="loading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Please wait. Loading your options...</p>
        <b-message type="is-danger" class="mt-6" v-else-if="error">{{ error }}</b-message>

        <!-- Done -->
        <div v-else-if="result" class="mt-6 pb-6">
            <ui-card>
                <h4 class="title is-5 mb-2"><i class="fas fa-check-circle has-text-success mr-2"></i>Hours Reallocated</h4>
                <p class="mb-4">
                    {{ result.moved }} clock hours from <strong>{{ source.display_name }}</strong> now live in the new enrollments below.
                    <template v-if="result.source.deleted"> The original enrollment has been removed.</template>
                    <template v-else> <strong>{{ result.source.kept_hours }}</strong> hours stay in the original enrollment.</template>
                    Each new enrollment starts with its own study plan.
                </p>
                <div class="selected-list">
                    <div class="selected-row" v-for="e in result.enrollments" :key="e.id">
                        <div class="selected-info">
                            <div class="selected-name">{{ e.name || e.title }}<span class="has-text-grey is-size-7 has-text-weight-normal" v-if="!e.name"> &middot; named at setup</span></div>
                            <div class="selected-org" v-if="e.name && e.name !== e.title">{{ e.title }}</div>
                        </div>
                        <div class="selected-actions">
                            <span class="hours-pill">{{ e.hours }} hours</span>
                            <b-button tag="router-link" :to="'/course/' + e.id" type="is-primary" size="is-small"><i class="fas fa-arrow-right mr-2"></i>Open</b-button>
                        </div>
                    </div>
                </div>
                <template #footer>
                    <div class="is-flex is-justify-content-flex-end" style="gap: 8px">
                        <b-button v-if="!result.source.deleted" tag="router-link" :to="{name: 'course-enrollment', params: {enrollmentId}}" size="is-small" outlined type="is-primary"><i class="fas fa-id-badge mr-2"></i>Back to Your Enrollment</b-button>
                        <b-button tag="router-link" :to="{name: 'enrollments'}" size="is-small" type="is-primary"><i class="fas fa-list mr-2"></i>View Course Enrollments</b-button>
                    </div>
                </template>
            </ui-card>
        </div>

        <!-- Not eligible -->
        <div v-else-if="!reallocation.eligible" class="mt-6 pb-6">
            <ui-card>
                <h4 class="title is-5 mb-2">Hours Cannot Be Reallocated Right Now</h4>
                <p>{{ reallocation.reason }}</p>
                <template #footer>
                    <div class="is-flex is-justify-content-flex-end">
                        <b-button tag="router-link" :to="{name: 'course-enrollment', params: {enrollmentId}}" size="is-small" type="is-primary" outlined><i class="fas fa-arrow-left mr-2"></i>Back to Your Enrollment</b-button>
                    </div>
                </template>
            </ui-card>
        </div>

        <!-- Picker -->
        <div v-else class="mt-6 pb-6">
            <ui-card>
                <div class="columns is-mobile mb-0">
                    <div class="column pb-0">
                        <div class="step-heading">Choose where the hours go</div>
                    </div>
                    <div class="column is-narrow pb-0">
                        <div class="alloc-status" :class="statusClass">{{ statusLabel }}</div>
                    </div>
                </div>
                <div class="step-sub">
                    <strong>{{ source.display_name }}</strong> has {{ reallocation.pool_hours }} clock hours.
                    <template v-if="reallocation.used_hours > 0">You have logged {{ reallocation.logged_hours }}, so {{ reallocation.used_hours }} must stay and up to <strong>{{ reallocation.max_move_hours }}</strong> can move.</template>
                    <template v-else>Nothing has been logged yet, so up to <strong>{{ reallocation.max_move_hours }}</strong> can move — all of them, if you like.</template>
                    Pick a course, set its hours and name, and add it. Every new enrollment needs at least {{ rules.min_hours_per_course }} hour.
                </div>

                <hour-progress class="mt-4" :target="reallocation.max_move_hours" :allocated="allocatedHours" :label="progressLabel"/>

                <!-- Where the hours go: the source strip on top, a spine down
                     its left with an arrow into each course added (the rows
                     are the working list — hours + remove live right there) -->
                <div class="hour-flow mt-3">
                    <div class="keep-note flow-source" :class="{'is-removing': keptHours === 0 && selections.length}">
                        <div class="keep-text">
                            <div class="source-name"><i class="fad fa-chalkboard-teacher mr-2"></i>{{ source.display_name }}</div>
                            <div class="source-note">
                                <template v-if="keptHours === 0 && selections.length"><i class="fas fa-triangle-exclamation mr-1"></i>Every hour is moving — this enrollment will be removed once you confirm.</template>
                                <template v-else-if="reallocation.min_keep_hours > 0">{{ reallocation.pool_hours }} clock hours &middot; <strong>{{ keptHours }}</strong> stay here ({{ reallocation.logged_hours }} logged)</template>
                                <template v-else>{{ reallocation.pool_hours }} clock hours &middot; <strong>{{ keptHours }}</strong> stay here unless placed</template>
                            </div>
                        </div>
                        <!-- Same action as the footer button; the catalog below is long -->
                        <b-button type="is-primary" size="is-small" :disabled="!canContinue" :loading="previewing" @click="review"><i class="fas fa-arrow-right mr-2"></i>Review and Confirm</b-button>
                    </div>

                    <div class="flow-list">
                        <div class="flow-row" v-for="(selection, i) in selections" :key="selection.key" :class="{'is-last': i === selections.length - 1 && remainingHours <= 0}">
                            <span class="spine-tail" v-if="!(i === selections.length - 1 && remainingHours <= 0)"></span>
                            <div class="selected-info">
                                <div class="selected-name">{{ selection.name }}<span class="has-text-grey is-size-7 has-text-weight-normal" v-if="selection.named_in_setup"> &middot; named at setup</span></div>
                                <div class="selected-org" v-if="selection.organization">{{ selection.organization }}</div>
                            </div>
                            <div class="selected-actions">
                                <b-input type="number" :value="selection.hours" @input="setHours(selection, $event)" @blur="clampHours(selection)" :min="rules.min_hours_per_course" :max="maxHoursFor(selection.id, selection.hours)" step="1" size="is-small" class="hours-input"/>
                                <span class="is-size-7 has-text-grey">hours</span>
                                <b-button type="is-danger is-light" size="is-small" @click="remove(selection)">Remove</b-button>
                            </div>
                        </div>
                        <div class="flow-row is-ghost is-last" v-if="remainingHours > 0">
                            <div class="selected-info">
                                <div class="selected-name">{{ selections.length ? 'Still available' : 'Add a course from the catalog below' }}</div>
                                <div class="selected-org">{{ remainingHours }} {{ selections.length ? 'more hours can move, or stay with the original course if you choose' : 'hours available to move — any you leave unplaced stay with the original course' }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="has-text-grey mt-4" v-if="loadingCatalog"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading the catalog...</div>
                <div v-else class="browse-section">
                    <div class="browse-heading"><i class="fad fa-browsers mr-2"></i>Browse the catalog</div>
                    <div class="browse-controls">
                        <b-select v-model="categoryFilter" expanded>
                            <option :value="null">All categories</option>
                            <option v-for="category in categoryOptions" :key="'cat-' + category.id" :value="category.id">{{ category.title }} ({{ category.count }})</option>
                        </b-select>
                        <b-input v-model="search" placeholder="Search courses..." icon="search" expanded/>
                    </div>
                    <div class="has-text-grey is-size-7 py-3" v-if="!browseCourses.length">No courses match.</div>
                    <div class="course-row" v-for="course in browseCourses" :key="course.id">
                        <div class="course-info">
                            <div class="course-title">{{ course.title }}</div>
                            <div class="course-org" v-if="course.organization">{{ course.organization.title }}</div>
                        </div>
                        <div class="course-actions">
                            <span class="in-package" v-if="instanceCount(course)">Added<template v-if="instanceCount(course) > 1"> &times;{{ instanceCount(course) }}</template></span>
                            <b-button size="is-small" @click="showInfo(course)"><i class="far fa-circle-info mr-2"></i>Info</b-button>
                            <b-button type="is-primary" size="is-small" :disabled="atLimit(course) || remainingHours < rules.min_hours_per_course" @click="choose(course)"><i class="fas fa-plus mr-2"></i>Add</b-button>
                        </div>
                    </div>
                </div>

                <template #footer>
                    <div class="is-flex is-justify-content-space-between is-align-items-flex-start" style="gap: 8px">
                        <b-button tag="router-link" :to="{name: 'course-enrollment', params: {enrollmentId}}" size="is-small" type="is-primary" outlined><i class="fas fa-arrow-left mr-2"></i>Cancel</b-button>
                        <b-button type="is-primary" size="is-small" :disabled="!canContinue" :loading="previewing" @click="review"><i class="fas fa-arrow-right mr-2"></i>Review and Confirm</b-button>
                    </div>
                </template>
            </ui-card>
        </div>

        <!-- Course info -->
        <b-modal v-model="infoOpen" :width="720" scroll="keep">
            <div class="card" v-if="infoCourse">
                <div class="card-content info-content">
                    <h4 class="title is-5 mb-1">{{ infoCourse.title }}</h4>
                    <div class="modal-org" v-if="infoCourse.organization">{{ infoCourse.organization.title }}</div>
                    <div class="has-text-grey mt-4" v-if="infoLoading"><i class="fas fa-spin fa-spinner fa-fw mr-3"></i>Loading...</div>
                    <div v-else-if="infoDetail" class="mt-4">
                        <div class="info-text" v-html="infoDetail.description"></div>
                        <div v-if="sdList('goals').length" class="mt-4">
                            <div class="info-header"><i class="fad fa-bullseye-arrow mr-2"></i>Goals</div>
                            <ul class="info-text"><li v-for="(g, i) in sdList('goals')" :key="'g' + i">{{ g }}</li></ul>
                        </div>
                        <div v-if="sdList('objectives').length" class="mt-4">
                            <div class="info-header"><i class="fad fa-list-check mr-2"></i>Objectives</div>
                            <ul class="info-text"><li v-for="(o, i) in sdList('objectives')" :key="'o' + i">{{ o }}</li></ul>
                        </div>
                    </div>
                    <div class="is-flex is-justify-content-flex-end mt-5">
                        <b-button @click="infoOpen = false">Close</b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Customize: hours + name -->
        <b-modal v-model="customizing" :width="560" scroll="keep">
            <div class="card" v-if="chosen">
                <div class="card-content">
                    <h4 class="title is-5 mb-1">{{ chosen.title }}</h4>
                    <div class="modal-org" v-if="chosen.organization">{{ chosen.organization.title }}</div>

                    <b-field class="mt-5" :message="'Between ' + rules.min_hours_per_course + ' and ' + chosenMax + ' hours'">
                        <template #label>Clock Hours <span class="has-text-grey has-text-weight-normal is-size-7">({{ remainingHours }} left to place)</span></template>
                        <b-input type="number" v-model.number="chosenHours" :min="rules.min_hours_per_course" :max="chosenMax" step="1" style="max-width: 160px"/>
                    </b-field>
                    <div class="chips mb-2">
                        <button type="button" class="chip" v-for="opt in quickHourOptions" :key="'q' + opt.value" :class="{'is-selected': chosenHours === opt.value}" @click="chosenHours = opt.value">{{ opt.label }}</button>
                    </div>

                    <div v-if="chosenNamedInSetup" class="mt-5">
                        <b-message type="is-info">
                            <i class="far fa-tag mr-2"></i>You'll name this course during course setup — each enrollment gets its own focus when you begin.
                        </b-message>
                    </div>
                    <div v-else-if="chosenOptionNaming" class="mt-5">
                        <div class="has-text-weight-bold mb-2">Course Name</div>
                        <p class="is-size-7 has-text-grey mb-3">Choose the option(s) that complete the name of this enrollment. Each enrollment must have a unique name.</p>
                        <div class="columns">
                            <div class="column is-6" v-if="suffix1Options.length">
                                <b-select v-model="custom1" expanded>
                                    <option value="" disabled>Select an option...</option>
                                    <option v-for="option in suffix1Options" :value="option" :key="option">{{ option }}</option>
                                </b-select>
                            </div>
                            <div class="column is-6" v-if="suffix2Options.length">
                                <b-select v-model="custom2" expanded>
                                    <option value="" disabled>Select an option...</option>
                                    <option v-for="option in suffix2Options" :value="option" :key="option">{{ option }}</option>
                                </b-select>
                            </div>
                        </div>
                    </div>
                    <div v-else class="mt-5">
                        <b-field>
                            <template #label>
                                Focus <span class="has-text-grey has-text-weight-normal is-size-7">(optional — added after the course name)</span>
                            </template>
                            <b-input v-model="customText" :placeholder="needsDistinct ? 'Required — you already have &quot;' + chosen.title + '&quot;' : 'e.g. Biology, Assessment, Spring Semester'"/>
                        </b-field>
                    </div>

                    <b-message type="is-danger" v-if="!chosenNamedInSetup && proposedName && nameTaken" class="mt-3">
                        <i class="far fa-exclamation-triangle mr-2"></i>You already have a course named <strong>{{ proposedName }}</strong>. Please choose a different name.
                    </b-message>
                    <b-message type="is-info" v-else-if="!chosenNamedInSetup && proposedName" class="mt-3">
                        <i class="far fa-tag mr-2"></i>This enrollment will be named: <strong>{{ proposedName }}</strong>
                    </b-message>

                    <div class="is-flex is-justify-content-space-between mt-5">
                        <b-button @click="customizing = false">Cancel</b-button>
                        <b-button type="is-primary" :disabled="!canAdd" @click="confirmAdd"><i class="fas fa-plus mr-2"></i>Add Course</b-button>
                    </div>
                </div>
            </div>
        </b-modal>

        <!-- Review + irreversible confirm (card body + the standard shaded CTA footer) -->
        <b-modal v-model="reviewing" :width="640" scroll="keep" :can-cancel="!submitting">
            <ui-card v-if="preview" class="confirm-card">
                <h4 class="title is-5 mb-3"><i class="fas fa-triangle-exclamation has-text-danger mr-2"></i>Confirm Reallocation</h4>

                <div class="review-source">
                    <div class="has-text-weight-bold">{{ preview.source.display_name }}</div>
                    <div v-if="preview.source.will_be_deleted" class="has-text-danger">All {{ preview.source.moved_hours }} hours move out. This enrollment will be <strong>removed</strong>.</div>
                    <div v-else>{{ preview.source.moved_hours }} of {{ preview.source.pool_hours }} hours move out &middot; <strong>{{ preview.source.kept_hours }}</strong> stay here<template v-if="preview.source.logged_hours > 0"> (you have logged {{ preview.source.logged_hours }})</template>.</div>
                </div>

                <div class="selected-list mt-4">
                    <div class="selected-row" v-for="(s, i) in preview.selections" :key="'r' + i">
                        <div class="selected-info">
                            <div class="selected-name">{{ s.name }}</div>
                            <div class="selected-org" v-if="s.name !== s.title">{{ s.title }}</div>
                        </div>
                        <div class="selected-actions"><span class="hours-pill">{{ s.hours }} hours</span></div>
                    </div>
                </div>

                <!-- Acknowledgement: same toggle-row pattern as the checkout terms -->
                <div class="irreversible mt-4">
                    <div class="term-row">
                        <div class="term-switch"><b-switch type="is-success" v-model="acknowledged"/></div>
                        <div class="term-text">
                            <strong>This cannot be reversed.</strong> Once confirmed, the hours moved to the new enrollments cannot be moved back into <strong>{{ preview.source.display_name }}</strong>. Each new enrollment will need its own study plan before you can begin. I understand this reallocation is permanent.
                        </div>
                    </div>
                </div>

                <b-message type="is-danger" v-if="submitError" class="mt-3">{{ submitError }}</b-message>

                <template #footer>
                    <div class="is-flex is-justify-content-space-between is-align-items-flex-start" style="gap: 8px">
                        <b-button @click="reviewing = false" :disabled="submitting" size="is-small" type="is-primary" outlined><i class="fas fa-arrow-left mr-2"></i>Go Back</b-button>
                        <b-button type="is-primary" size="is-small" :disabled="!acknowledged" :loading="submitting" @click="submit"><i class="fas fa-arrows-turn-right mr-2"></i>Reallocate {{ preview.source.moved_hours }} Hours</b-button>
                    </div>
                </template>
            </ui-card>
        </b-modal>
    </div>
</template>

<script>
    import {mapActions} from "vuex";
    import UiCard from "../../components/UiCard.vue";
    import HourProgress from "../../components/HourProgress.vue";
    import {client as http} from "../../http_client";
    import {
        isNamedInSetup, optionNaming, suffixOptions, optionNamingComplete,
        proposedEnrollmentName, normalizeName
    } from "../../utils/courseNaming";

    const QUICK_HOURS = [10, 20, 30, 40, 50];

    /**
     * Reallocate Hours: move the unused clock hours of one self-directed
     * enrollment into new enrollments. The picker is the bundle builder's
     * course step (browse → choose → hours + name → add) with a local
     * selection list; the pool is whatever the api says may move.
     * Everything is re-validated server-side (preview, then execute).
     */
    export default {
        name: "ReallocateHours",
        components: {UiCard, HourProgress},
        props: ['enrollmentId'],
        data() {
            return {
                loading: true,
                loadingCatalog: true,
                error: null,
                source: null,
                rules: {min_hours_per_course: 1, max_instances_per_course: 3},
                reallocation: null,
                eligible: {},          // course id -> {max_hours, remaining_instances, named_in_setup, name_reuse_after}
                takenEnrollments: [],  // [{enrollment_id, name, created_at}] from the api
                catalog: [],
                selections: [],
                nextKey: 1,
                categoryFilter: null,
                search: '',
                // customize modal
                customizing: false,
                chosen: null,
                chosenHours: 1,
                custom1: '',
                custom2: '',
                customText: '',
                // info modal
                infoOpen: false,
                infoCourse: null,
                infoDetail: null,
                infoLoading: false,
                // review
                reviewing: false,
                previewing: false,
                preview: null,
                acknowledged: false,
                submitting: false,
                submitError: null,
                result: null
            };
        },
        computed: {
            allocatedHours() {
                return this.selections.reduce((sum, s) => sum + (parseInt(s.hours) || 0), 0);
            },
            remainingHours() {
                return this.reallocation.max_move_hours - this.allocatedHours;
            },
            keptHours() {
                return this.reallocation.pool_hours - this.allocatedHours;
            },
            statusLabel() {
                if (!this.selections.length) return 'Nothing added yet';
                if (this.remainingHours > 0) return this.remainingHours + ' hours still available';
                if (this.remainingHours < 0) return Math.abs(this.remainingHours) + ' hours over — lower some';
                return 'All available hours placed ✓';
            },
            statusClass() {
                if (this.remainingHours < 0) return 'is-over';
                if (this.remainingHours === 0 && this.selections.length) return 'is-full';
                return '';
            },
            progressLabel() {
                return `${this.allocatedHours} of up to ${this.reallocation.max_move_hours} available hours placed`;
            },
            canContinue() {
                return this.selections.length > 0 && this.allocatedHours >= this.rules.min_hours_per_course && this.remainingHours >= 0
                    && this.selections.every(s => parseInt(s.hours) >= this.rules.min_hours_per_course);
            },
            // Courses the api allows, joined with the catalog rows (title, org, categories, naming config)
            courses() {
                return this.catalog.filter(c => this.eligible[c.id]);
            },
            categoryOptions() {
                const byId = {};
                for (const course of this.courses) {
                    for (const category of (course.categories || [])) {
                        if (!byId[category.id]) byId[category.id] = {id: category.id, title: category.title, count: 0};
                        byId[category.id].count++;
                    }
                }
                return Object.values(byId).sort((a, b) => a.title.localeCompare(b.title));
            },
            browseCourses() {
                let list = this.courses;
                if (this.categoryFilter !== null) {
                    list = list.filter(c => (c.categories || []).some(cat => cat.id === this.categoryFilter));
                }
                if (this.search.trim()) {
                    const q = this.search.trim().toLowerCase();
                    list = list.filter(c => c.title.toLowerCase().includes(q));
                }
                return list;
            },
            // Names in use: the student's enrollments (server list) still
            // inside the chosen course's repeat window + what's already added
            // here. A source that is being converted 100% is about to be
            // removed, so its own name becomes available.
            takenNameSet() {
                const info = this.chosen ? this.eligible[this.chosen.id] : null;
                const cutoff = info && info.name_reuse_after ? new Date(info.name_reuse_after) : null;
                const names = new Set();
                for (const t of this.takenEnrollments) {
                    if (this.keptHours === 0 && t.enrollment_id === this.source.id) continue;
                    if (cutoff && new Date(t.created_at) < cutoff) continue;
                    names.add(normalizeName(t.name).toLowerCase());
                }
                for (const s of this.selections) {
                    if (!s.named_in_setup) names.add(normalizeName(s.name).toLowerCase());
                }
                return names;
            },
            chosenNamedInSetup() {
                return isNamedInSetup(this.chosen);
            },
            chosenOptionNaming() {
                return optionNaming(this.chosen);
            },
            suffix1Options() {
                return suffixOptions(this.chosen, 1);
            },
            suffix2Options() {
                return suffixOptions(this.chosen, 2);
            },
            namingComplete() {
                return optionNamingComplete(this.chosen, this.custom1, this.custom2);
            },
            needsDistinct() {
                if (!this.chosen || this.chosenOptionNaming || this.chosenNamedInSetup) return false;
                return this.takenNameSet.has(normalizeName(this.chosen.title).toLowerCase());
            },
            proposedName() {
                return proposedEnrollmentName(this.chosen, {custom1: this.custom1, custom2: this.custom2, customText: this.customText});
            },
            nameTaken() {
                if (!this.proposedName || this.chosenNamedInSetup) return false;
                return this.takenNameSet.has(this.proposedName.toLowerCase());
            },
            chosenMax() {
                return this.chosen ? this.maxHoursFor(this.chosen.id, 0) : this.rules.min_hours_per_course;
            },
            quickHourOptions() {
                const out = QUICK_HOURS.filter(h => h <= this.chosenMax && h >= this.rules.min_hours_per_course).map(h => ({value: h, label: h + ' hours'}));
                if (this.chosenMax >= this.rules.min_hours_per_course && !out.some(o => o.value === this.chosenMax)) {
                    out.push({value: this.chosenMax, label: `All ${this.chosenMax} remaining`});
                }
                return out;
            },
            canAdd() {
                const hours = parseInt(this.chosenHours);
                return !!this.chosen && !this.nameTaken && this.namingComplete && !!this.proposedName
                    && hours >= this.rules.min_hours_per_course && hours <= this.chosenMax;
            }
        },
        watch: {
            enrollmentId() {
                this.load();
            }
        },
        methods: {
            ...mapActions(['getEnrollments']),
            load() {
                this.loading = true;
                this.error = null;
                http.get(`app/enrollments/${this.enrollmentId}/reallocation`, {force: true}).then(response => {
                    const data = response.data;
                    this.source = data.source;
                    this.rules = data.rules;
                    this.reallocation = data.reallocation;
                    this.takenEnrollments = data.taken_enrollments || [];
                    const eligible = {};
                    for (const c of (data.courses || [])) eligible[c.id] = c;
                    this.eligible = eligible;
                    this.loading = false;
                    if (this.reallocation.eligible) this.loadCatalog();
                }).catch(err => {
                    this.error = (err.response && err.response.data && err.response.data.error) || 'Could not load reallocation options.';
                    this.loading = false;
                });
            },
            loadCatalog() {
                this.loadingCatalog = true;
                http.get('app/courses/new', {force: true}).then(response => {
                    this.catalog = (response.data || []).slice().sort((a, b) => (a.title.toUpperCase() > b.title.toUpperCase()) ? 1 : -1);
                    this.loadingCatalog = false;
                }).catch(() => {
                    this.loadingCatalog = false;
                });
            },
            instanceCount(course) {
                return this.selections.filter(s => s.id === course.id).length;
            },
            atLimit(course) {
                const info = this.eligible[course.id];
                const allowed = info ? Math.min(this.rules.max_instances_per_course, info.remaining_instances) : 0;
                return this.instanceCount(course) >= allowed;
            },
            // Most hours one allocation on this course may take: what is still
            // unplaced (plus its own current hours when editing), within the
            // course's largest tier
            maxHoursFor(courseId, currentHours) {
                const info = this.eligible[courseId];
                let max = this.remainingHours + (parseInt(currentHours) || 0);
                if (info && info.max_hours) max = Math.min(max, info.max_hours);
                return Math.max(this.rules.min_hours_per_course, max);
            },
            // Typing is left alone (a half-typed "1" must not snap to the max);
            // the value is clamped into range when the field loses focus
            setHours(selection, value) {
                const hours = parseInt(value);
                if (!Number.isInteger(hours)) return;
                selection.hours = hours;
            },
            clampHours(selection) {
                const hours = parseInt(selection.hours);
                const max = this.maxHoursFor(selection.id, Number.isInteger(hours) ? hours : 0);
                selection.hours = Number.isInteger(hours) ? Math.max(this.rules.min_hours_per_course, Math.min(hours, max)) : this.rules.min_hours_per_course;
            },
            remove(selection) {
                this.selections = this.selections.filter(s => s.key !== selection.key);
            },
            choose(course) {
                this.chosen = course;
                const max = this.maxHoursFor(course.id, 0);
                // Default to the largest round option that fits, else everything left
                const snapped = QUICK_HOURS.filter(h => h <= max).pop();
                this.chosenHours = snapped || max;
                this.custom1 = '';
                this.custom2 = '';
                this.customText = '';
                this.customizing = true;
            },
            showInfo(course) {
                this.infoCourse = course;
                this.infoDetail = null;
                this.infoLoading = true;
                this.infoOpen = true;
                http.get('app/courses/new/' + course.id).then(response => {
                    this.infoDetail = response.data;
                    this.infoLoading = false;
                }).catch(() => {
                    this.infoLoading = false;
                });
            },
            sdList(key) {
                const detail = this.infoDetail;
                const cfg = detail && detail.self_directed_config;
                const list = cfg && Array.isArray(cfg[key]) ? cfg[key] : [];
                return list.map(item => (item && item.text) ? item.text : item).filter(Boolean);
            },
            confirmAdd() {
                if (!this.canAdd) return;
                const selection = {
                    key: this.nextKey++,
                    id: this.chosen.id,
                    title: this.chosen.title,
                    name: this.proposedName,
                    named_in_setup: this.chosenNamedInSetup,
                    organization: this.chosen.organization ? this.chosen.organization.title : null,
                    hours: parseInt(this.chosenHours)
                };
                // Close first so the modal never flags the new name as its own conflict
                this.customizing = false;
                this.chosen = null;
                this.selections.push(selection);
            },
            payload() {
                return {
                    selections: this.selections.map(s => ({course_id: s.id, name: s.named_in_setup ? '' : s.name, hours: parseInt(s.hours)}))
                };
            },
            review() {
                if (!this.canContinue) return;
                this.previewing = true;
                this.submitError = null;
                http.post(`app/enrollments/${this.enrollmentId}/reallocation/preview`, this.payload(), {force: true}).then(response => {
                    this.previewing = false;
                    this.preview = response.data;
                    this.acknowledged = false;
                    this.reviewing = true;
                }).catch(err => {
                    this.previewing = false;
                    this.$buefy.dialog.alert({
                        title: 'Please Check Your Selections',
                        message: (err.response && err.response.data && err.response.data.error) || 'Your selections could not be validated.',
                        type: 'is-warning',
                        confirmText: 'OK'
                    });
                });
            },
            submit() {
                if (!this.acknowledged || this.submitting) return;
                this.submitting = true;
                this.submitError = null;
                http.post(`app/enrollments/${this.enrollmentId}/reallocation`, {...this.payload(), acknowledged: true}, {force: true}).then(response => {
                    this.submitting = false;
                    this.reviewing = false;
                    this.result = {
                        moved: this.preview.source.moved_hours,
                        source: response.data.source,
                        enrollments: response.data.enrollments
                    };
                    // The Course Enrollments list changed underneath us
                    this.getEnrollments();
                }).catch(err => {
                    this.submitting = false;
                    this.submitError = (err.response && err.response.data && err.response.data.error) || 'The reallocation could not be completed. Nothing was changed.';
                });
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .step-heading {
        font-size: 1.25rem;
        font-weight: 900;
        color: #33475b;
    }

    .step-sub {
        font-size: 0.9rem;
        color: #666d7a;
        margin-top: 0.4rem;
    }

    .alloc-status {
        font-size: 0.85rem;
        font-weight: bold;
        color: #7a8a9d;

        &.is-full {
            color: #2e7d4f;
        }

        &.is-over {
            color: #c0392b;
        }
    }

    // The source enrollment (top of the flow). Shaded strip; tinted red when
    // every hour is leaving.
    .keep-note {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 12px;
        background: #eef3f8;
        border: 1px solid #c9d8e8;
        border-radius: 8px;
        padding: 0.8rem 1rem;
        font-size: 0.9rem;
        color: #33475b;

        .keep-text {
            flex: 1;
            min-width: 0;

            .source-name {
                font-weight: bold;
                font-size: 0.95rem;

                i {
                    color: #2e5f8f;
                }
            }

            .source-note {
                font-size: 0.85rem;
                color: #a86d24;
                margin-top: 2px;
            }
        }

        .button {
            flex-shrink: 0;
        }

        @media (max-width: 560px) {
            flex-direction: column;
        }

        &.is-removing {
            background: #fdf1f0;
            border-color: #f1c7c4;

            .keep-text .source-note {
                color: #c0392b;
            }
        }
    }

    // Spine + branch arrows from the source into each added course
    $spine-x: 22px;
    $branch-w: 26px;
    $row-gap: 10px;
    $flow-green: #2e7d4f;
    $flow-ghost: #b5c0cc;

    .flow-list {
        margin-left: $spine-x;
        padding-left: $branch-w;

        .flow-row {
            position: relative;
            display: flex;
            align-items: flex-start;
            gap: 16px;
            margin-top: $row-gap;
            padding: 0.6rem 0.9rem;
            background: #e7f4ec;
            border: 1px solid $flow-green;
            border-radius: 8px;

            // Branch: down from the row above (or the source), then across
            &::before {
                content: "";
                position: absolute;
                left: -($branch-w + 1px);
                top: -($row-gap + 1px);
                width: $branch-w - 8px;
                height: calc(50% + #{$row-gap});
                border-left: 2.5px solid $flow-green;
                border-bottom: 2.5px solid $flow-green;
                border-radius: 0 0 0 8px;
            }

            // Arrowhead at the row's edge
            &::after {
                content: "";
                position: absolute;
                left: -10px;
                top: 50%;
                margin-top: -6px;
                border: 6px solid transparent;
                border-left: 9px solid $flow-green;
                border-right: 0;
            }

            // Spine continues down past the row toward the next branch
            // (stops at the row's bottom edge — the next row's branch bridges
            // the gap, so a dashed ghost branch never overlaps a solid tail)
            .spine-tail {
                position: absolute;
                left: -($branch-w + 1px);
                top: 50%;
                bottom: -1px;
                border-left: 2.5px solid $flow-green;
            }

            .selected-info {
                min-width: 0;
                flex: 1;

                .selected-name {
                    font-size: 0.95rem;
                    font-weight: bold;
                    color: #1f5a3a;
                }

                .selected-org {
                    font-size: 0.75rem;
                    color: #2e7d4f;
                    margin-top: 1px;
                }
            }

            .selected-actions {
                display: flex;
                align-items: center;
                gap: 10px;
                flex-shrink: 0;

                .hours-input {
                    width: 88px;
                }
            }

            &.is-ghost {
                background: #ffffff;
                border: 1px dashed $flow-ghost;

                &::before {
                    border-left: 2.5px dashed $flow-ghost;
                    border-bottom: 2.5px dashed $flow-ghost;
                }

                &::after {
                    border-left-color: $flow-ghost;
                }

                .selected-info .selected-name {
                    color: #7a8a9d;
                }

                .selected-info .selected-org {
                    color: #9aa6b3;
                }
            }
        }
    }

    .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .chip {
            font-family: inherit;
            font-size: 0.8rem;
            font-weight: bold;
            padding: 6px 12px;
            border-radius: 999px;
            cursor: pointer;
            border: 1px solid #c4cfda;
            background: #ffffff;
            color: #33475b;

            &:hover {
                border-color: #7fa8cd;
            }

            &.is-selected {
                border-color: #2e5f8f;
                background: #2e5f8f;
                color: #ffffff;
            }
        }
    }

    .selected-list {
        border: 1px solid #d6e4f2;
        background: #f7fafd;
        border-radius: 6px;
        padding: 0.4rem 1rem;

        .selected-row {
            display: flex;
            align-items: flex-start;
            gap: 16px;
            padding: 0.6rem 0;
            border-bottom: 1px solid #e1e9f2;

            &:last-child {
                border-bottom: none;
            }

            .selected-info {
                min-width: 0;
                flex: 1;

                .selected-name {
                    font-size: 0.95rem;
                    font-weight: bold;
                    color: #33475b;
                }

                .selected-org {
                    font-size: 0.75rem;
                    color: #7a8a9d;
                    margin-top: 1px;
                }
            }

            .selected-actions {
                display: flex;
                align-items: center;
                gap: 10px;
                flex-shrink: 0;

                .hours-input {
                    width: 88px;
                }
            }
        }
    }

    .hours-pill {
        font-size: 0.75rem;
        font-weight: bold;
        color: #2e5f8f;
        background: #e8f0f8;
        padding: 3px 10px;
        border-radius: 999px;
        white-space: nowrap;
    }

    .browse-section {
        margin-top: 1.4rem;

        .browse-heading {
            font-size: 0.95rem;
            font-weight: 900;
            color: #33475b;
            margin-bottom: 0.5rem;

            i {
                color: #2e5f8f;
            }
        }

        .browse-controls {
            display: flex;
            gap: 12px;
            margin-bottom: 0.5rem;
            flex-wrap: wrap;

            > * {
                flex: 1;
                min-width: 200px;
            }
        }
    }

    .course-row {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        padding: 0.7rem 0;
        border-bottom: 1px solid #eef1f5;

        &:last-child {
            border-bottom: none;
        }

        .course-info {
            min-width: 0;
            flex: 1;

            .course-title {
                font-size: 0.95rem;
                font-weight: bold;
                color: #33475b;
            }

            .course-org {
                font-size: 0.75rem;
                color: #7a8a9d;
                margin-top: 1px;
            }
        }

        .course-actions {
            display: flex;
            align-items: center;
            gap: 12px;
            flex-shrink: 0;

            .in-package {
                font-size: 0.75rem;
                font-weight: bold;
                color: #2e7d4f;
                background: #e7f4ec;
                padding: 3px 10px;
                border-radius: 999px;
                white-space: nowrap;
            }
        }
    }

    .modal-org {
        font-size: 0.8rem;
        color: #7a8a9d;
    }

    .info-content {
        max-height: 80vh;
        overflow-y: auto;

        .info-header {
            font-size: 0.95rem;
            font-weight: 900;
            color: #33475b;
            margin-bottom: 0.5rem;

            i {
                color: #2e5f8f;
            }
        }

        .info-text {
            font-size: 0.9rem;
            color: #4a4a4a;

            li {
                margin: 0.25rem 0 0.25rem 1.2rem;
                list-style: disc;
            }
        }
    }

    .review-source {
        background: #f4f6f9;
        border: 1px solid #e6eaef;
        border-radius: 6px;
        padding: 0.8rem 1rem;
        font-size: 0.95rem;
    }

    .irreversible {
        background: #fdf1f0;
        border: 1px solid #f1c7c4;
        border-radius: 6px;
        padding: 0.9rem 1rem;
        color: #6b1f1a;
        font-size: 0.9rem;

        .term-row {
            display: flex;
            align-items: flex-start;
            gap: 12px;

            .term-switch {
                flex-shrink: 0;
                padding-top: 2px;
            }

            .term-text {
                flex: 1;
                min-width: 0;
            }
        }
    }

    // The modal card carries its own shadow from the modal; keep the footer bar
    .confirm-card {
        ::v-deep .ui-card-footer {
            padding: 0.75rem 1.5rem;
        }
    }
</style>
